import { Modal, BodyLargeEmphasized } from '@/design-system';
import { GradeRangeSelector } from '@/components';
import { Divider } from '../Divider';
import { FilterOption } from './FilterOption';
import { css } from '@emotion/css';
import type { OptionType } from './FilterOption';
import { Grade } from '@/pages/AccountInformation/AccountInformation.types';

type Props = {
  isOpen: boolean;
  title: string;
  viewResultsLabel: string;
  onClearFilters: () => void;
  onShowResults: () => void;
  onToggle: (state: boolean) => void;
  onSelectTypesToggle: (value: string, description: string) => () => void;
  disableClearAllButton: boolean;
  flyerType: string;
  types: OptionType[];
  selectedTypes: string[];
  grades: Grade[];
  gradeFilter: { from?: string; to?: string };
  initialGradeValue?: { from?: string; to?: string };
  handleGradeChange: (field: 'from' | 'to', value?: string) => void;
  facetsLoading: boolean;
};

export const ModalFilter: React.FC<Props> = ({
  isOpen,
  title,
  viewResultsLabel,
  onClearFilters,
  onShowResults,
  onToggle,
  onSelectTypesToggle,
  disableClearAllButton,
  types,
  selectedTypes,
  grades,
  gradeFilter,
  handleGradeChange,
  facetsLoading,
  initialGradeValue,
}) => {
  const midpoint = Math.ceil(types.length / 2);
  const firstColumn = types.slice(0, midpoint);
  const secondColumn = types.slice(midpoint);

  return (
    <Modal
      isOpen={isOpen}
      title={title}
      confirmButtonText={viewResultsLabel}
      cancelButtonText="Reset Filters"
      onCancel={onClearFilters}
      onConfirm={onShowResults}
      onClose={() => onToggle(false)}
      disableCancelButton={disableClearAllButton}
      loading={facetsLoading}
    >
      <div className={styles.container}>
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>
            <BodyLargeEmphasized className={styles.filterName}>
              Grades
            </BodyLargeEmphasized>
          </legend>
          <div className={styles.gradeSelectorContainer}>
            <GradeRangeSelector
              fromGrade={gradeFilter.from}
              toGrade={gradeFilter.to}
              onChange={handleGradeChange}
              grades={grades}
              initialValue={initialGradeValue}
            />
          </div>
          <div className={styles.divider}>
            <Divider />
          </div>
        </fieldset>
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>
            <BodyLargeEmphasized className={styles.filterName}>
              Types
            </BodyLargeEmphasized>
          </legend>
          <div className={styles.filtersContainer}>
            <div className={styles.flexColumn}>
              {firstColumn.map(({ value, description, disabled }) => (
                <FilterOption
                  key={value}
                  value={value}
                  description={description}
                  filterName="types"
                  checked={selectedTypes.includes(value)}
                  handleToggle={() => onSelectTypesToggle(value, description)}
                  disabled={disabled && !selectedTypes.includes(value)}
                />
              ))}
            </div>
            <div className={styles.flexColumn}>
              {secondColumn.map(({ value, description, disabled }) => (
                <FilterOption
                  key={value}
                  value={value}
                  description={description}
                  filterName="types"
                  checked={selectedTypes.includes(value)}
                  handleToggle={() => onSelectTypesToggle(value, description)}
                  disabled={disabled && !selectedTypes.includes(value)}
                />
              ))}
            </div>
          </div>
        </fieldset>
      </div>
    </Modal>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
  `,
  filterName: css`
    text-transform: capitalize;
  `,
  gradeSelectorContainer: css`
    padding-top: 16px;
  `,
  filtersContainer: css`
    display: flex;
    flex-wrap: wrap;
    padding-top: 16px;
  `,
  divider: css`
    padding: 24px 0;
  `,
  fieldset: css`
    border: 0;
    margin: 0;
    padding: 0;
    min-inline-size: 0;
  `,
  legend: css`
    margin: 0;
    padding: 0;
  `,
  flexColumn: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    padding-right: 10px;

    &:last-child {
      padding-right: 0;
    }
  `,
};
